/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { FLOW_TYPES } from '../../utils/constants';

// Storage key constants
const VASS_CURRENT_UUID_KEY = 'vass_current_uuid';
const VASS_FORM_KEY_PREFIX = 'vass_form';

const PII_FIELDS = ['lastName', 'dob', 'veteranContactEmail'];

/**
 * Sanitize PII data for storage by removing PII fields
 * @param {object} data
 * @returns {object}
 */
const sanitizeForStorage = data => {
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => !PII_FIELDS.includes(key)),
  );
};

// Safe sessionStorage helpers
const getSessionItem = key => {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    return null;
  }
};

const setSessionItem = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Handle storage errors (e.g., quota exceeded, private browsing)
  }
};

const removeSessionItem = key => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    // Handle storage errors
  }
};

/**
 * Get the storage key for the current session
 * @returns {string}
 */
const getSessionStorageKey = () => VASS_FORM_KEY_PREFIX;

/**
 * Save form data to sessionStorage keyed by UUID
 * @param {string} uuid
 * @param {object} data
 */
const saveFormDataToStorage = (uuid, data) => {
  if (!uuid) return;
  setSessionItem(VASS_CURRENT_UUID_KEY, uuid);
  setSessionItem(getSessionStorageKey(), sanitizeForStorage(data));
};

/**
 * Load form data from sessionStorage for the current session
 * @returns {object|null}
 */
export const loadFormDataFromStorage = () => {
  const currentUuid = getSessionItem(VASS_CURRENT_UUID_KEY);
  if (!currentUuid) return null;
  return getSessionItem(getSessionStorageKey());
};

/**
 * Clear form data from sessionStorage for the current session
 */
const clearFormDataFromStorage = () => {
  removeSessionItem(getSessionStorageKey());
  removeSessionItem(VASS_CURRENT_UUID_KEY);
};

/** @typedef {{ dtStartUtc: string | null, dtEndUtc: string | null }} Slot */
/** @typedef {{ topicId: string, topicName: string }} Topic */
/** @typedef {{ selectedSlot: Slot, selectedTopics: Topic[], obfuscatedEmail: string | null, uuid: string | null, token: string | null, lastName: string | null, dob: string | null, veteranContactEmail: string | null, invitationData: string | null, flowType: string | null }} */
const initialState = {
  hydrated: false,
  sessionInvalidated: false,
  selectedSlot: {
    dtStartUtc: null,
    dtEndUtc: null,
  },
  selectedTopics: [],
  obfuscatedEmail: null,
  uuid: null,
  lastName: null,
  dob: null,
  veteranContactEmail: null,
  invitationData: null,
  flowType: FLOW_TYPES.ANY,
};

export const formSlice = createSlice({
  name: 'vassForm',
  initialState,
  reducers: {
    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
      if (state.uuid) {
        saveFormDataToStorage(state.uuid, {
          ...state,
          selectedSlot: {
            dtStartUtc: action.payload.dtStartUtc,
            dtEndUtc: action.payload.dtEndUtc,
          },
        });
      }
    },
    setSelectedTopics: (state, action) => {
      state.selectedTopics = action.payload;
      if (state.uuid) {
        saveFormDataToStorage(state.uuid, {
          ...state,
          selectedTopics: action.payload,
        });
      }
    },
    setLowAuthFormData: (state, action) => {
      state.uuid = action.payload.uuid;
      state.lastName = action.payload.lastName;
      state.dob = action.payload.dob;
      state.veteranContactEmail = action.payload.veteranContactEmail;
      state.invitationData = action.payload.invitationData;
      state.obfuscatedEmail = action.payload.obfuscatedEmail;
      if (state.uuid) {
        saveFormDataToStorage(state.uuid, {
          ...state,
          uuid: action.payload.uuid,
          veteranContactEmail: action.payload.veteranContactEmail,
          invitationData: action.payload.invitationData,
          obfuscatedEmail: action.payload.obfuscatedEmail,
        });
      }
    },
    setFlowType: (state, action) => {
      state.flowType = action.payload;
    },
    // Cookie changes don't re-render React, so withAuthorization watches this flag.
    invalidateSession: state => {
      state.sessionInvalidated = true;
    },
    clearFormData: state => {
      // Clear from storage before resetting state
      clearFormDataFromStorage();
      state.selectedSlot = {
        dtStartUtc: null,
        dtEndUtc: null,
      };
      state.selectedTopics = [];
      state.obfuscatedEmail = null;
      state.uuid = null;
      state.lastName = null;
      state.dob = null;
      state.veteranContactEmail = null;
      state.invitationData = null;
      state.hydrated = false;
      state.sessionInvalidated = false;
      state.flowType = FLOW_TYPES.ANY;
    },
    hydrateFormData: (state, action) => {
      state.hydrated = true;
      if (action.payload.uuid) {
        state.uuid = action.payload.uuid;
      }
      if (action.payload.lastName) {
        state.lastName = action.payload.lastName;
      }
      if (action.payload.dob) {
        state.dob = action.payload.dob;
      }
      if (action.payload.selectedSlot) {
        state.selectedSlot = action.payload.selectedSlot;
      }
      if (action.payload.obfuscatedEmail) {
        state.obfuscatedEmail = action.payload.obfuscatedEmail;
      }
      if (action.payload.veteranContactEmail) {
        state.veteranContactEmail = action.payload.veteranContactEmail;
      }
      if (action.payload.invitationData) {
        state.invitationData = action.payload.invitationData;
      }
      if (action.payload.selectedTopics) {
        state.selectedTopics = action.payload.selectedTopics;
      }
      if (action.payload.flowType) {
        state.flowType = action.payload.flowType;
      }
    },
  },
});

export const {
  setSelectedSlot,
  setSelectedTopics,
  setLowAuthFormData,
  setFlowType,
  invalidateSession,
  clearFormData,
  hydrateFormData,
} = formSlice.actions;

export const selectSelectedSlot = state => state.vassForm.selectedSlot;
export const selectSelectedTopics = state => state.vassForm.selectedTopics;
export const selectUuid = state => state.vassForm.uuid;
export const selectHydrated = state => state.vassForm.hydrated;
export const selectObfuscatedEmail = state => state.vassForm.obfuscatedEmail;
export const selectLastName = state => state.vassForm.lastName;
export const selectDob = state => state.vassForm.dob;
export const selectVeteranContactEmail = state =>
  state.vassForm.veteranContactEmail;
export const selectInvitationData = state => state.vassForm.invitationData;
export const selectFlowType = state => state.vassForm.flowType;
export const selectSessionInvalidated = state =>
  state.vassForm.sessionInvalidated;

export default formSlice.reducer;
